import React from "react";
import { Row, Col, Button, ListGroup } from "react-bootstrap";
import Moment from "moment";
import { PacmanLoader } from "react-spinners";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import "containers/common/List.css";
import "containers/common/Table.css";

export default function PropertyIntegrationsList({
  data,
  isLoading,
  isAuthenticated,
  controlType,
  deleteRecord,
  ctx,
  acceptIntegration,
  rejectIntegration,
}) {
  const dataSet = "integrations";
  const tableData = data || [];
  const columnHelper = createColumnHelper();

  const getStatusString = (integration) => {
    if (integration.is_accepted === null) return "pending";
    if (integration.is_accepted === true && integration.accept_reject_by !== "system") return "accepted";
    if (integration.is_accepted === true && integration.accept_reject_by === "system") return "auto-accepted";
    if (integration.is_accepted === false) return "rejected";
    return "pending";
  };

  const renderStatusButton = (row) => {
    const record = row.original;

    if (record.is_accepted === null) {
      return (
        <Button
          id={`property-integrations-status-${record.property_integration_id}`}
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => acceptIntegration(row)}
        >
          Pending
        </Button>
      );
    }

    if (record.is_accepted === true && record.accept_reject_by !== "system") {
      return (
        <Button
          id={`property-integrations-status-${record.property_integration_id}`}
          type="button"
          size="sm"
          variant="success"
          onClick={() => rejectIntegration(row)}
        >
          Accepted
        </Button>
      );
    }

    if (record.is_accepted === true && record.accept_reject_by === "system") {
      return (
        <Button
          id={`property-integrations-status-${record.property_integration_id}`}
          type="button"
          size="sm"
          variant="success"
          onClick={() => rejectIntegration(row)}
        >
          Auto-Accepted
        </Button>
      );
    }

    return (
      <Button
        id={`property-integrations-status-${record.property_integration_id}`}
        type="button"
        size="sm"
        variant="danger"
        onClick={() => acceptIntegration(row)}
      >
        Rejected
      </Button>
    );
  };

  const columns = [
    columnHelper.accessor("property_integration_id", {
      id: "property_integration_id",
      header: "ID",
      size: 65,
    }),
    columnHelper.accessor("value_to_match", {
      id: "value_to_match",
      header: "CoLin Address",
      size: 180,
      meta: { wrap: true },
    }),
    columnHelper.accessor("integration_property_id", {
      id: "integration_property_id",
      header: "CoreLogic Property ID",
      size: 120,
    }),
    columnHelper.accessor("integration_address", {
      id: "integration_address",
      header: "CoreLogic Address",
      cell: (info) => (
        <a
          href={`https://rpp.corelogic.com.au/property/${info.row.original.integration_property_id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {info.getValue()}
        </a>
      ),
      size: 220,
      meta: { wrap: true },
    }),
    columnHelper.accessor("integration_parcel_identifier", {
      id: "integration_parcel_identifier",
      header: "Parcel",
      size: 90,
    }),
    columnHelper.accessor("match_method", {
      id: "match_method",
      header: "Matched By",
      size: 110,
    }),
    columnHelper.accessor("parse_status", {
      id: "parse_status",
      header: "Parsing Status",
      size: 110,
    }),
    columnHelper.accessor("match_type", {
      id: "match_type",
      header: "Match Type",
      size: 90,
    }),
    columnHelper.accessor("match_rule", {
      id: "match_rule",
      header: "Match Rule",
      size: 90,
    }),
    columnHelper.accessor("created_date", {
      id: "created_date",
      header: "Date Created",
      cell: (info) => {
        const value = info.getValue();
        return value ? Moment(value).local().format("DD-MM-YYYY") : null;
      },
      size: 95,
    }),
    columnHelper.accessor("last_edited_date", {
      id: "last_edited_date",
      header: "Last Edited",
      cell: (info) => {
        const value = info.getValue();
        return value ? Moment(value).local().format("DD-MM-YYYY") : null;
      },
      size: 95,
    }),
    columnHelper.accessor("last_edited_by", {
      id: "last_edited_by",
      header: "Edited By",
      size: 110,
    }),
    columnHelper.display({
      id: "integration_status",
      header: "",
      cell: ({ row }) => renderStatusButton(row),
      size: 130,
      enableSorting: false,
    }),
    columnHelper.display({
      id: "delete_integration",
      header: "",
      cell: ({ row }) => (
        <Button
          id={`property-integrations-delete-${row.original.property_integration_id}`}
          type="button"
          size="sm"
          variant="danger"
          onClick={async (event) => {
            event.preventDefault();
            const confirmed = window.confirm("Are you sure you want to delete this record?");
            if (!confirmed) {
              return;
            }
            await deleteRecord(dataSet, row.original);
          }}
        >
          Delete
        </Button>
      ),
      size: 85,
      enableSorting: false,
    }),
  ];

  function renderList(rows) {
    if (!rows) {
      return null;
    }

    return [{}].concat(rows).map((integration, index) =>
      index !== 0 ? (
        <ListGroup.Item key={integration.property_integration_id || index}>
          <a
            href={`https://rpp.corelogic.com.au/property/${integration.integration_property_id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {integration.integration_property_id}: {integration.integration_address}
          </a>{" "}
          ({getStatusString(integration)})
        </ListGroup.Item>
      ) : (
        ""
      )
    );
  }

  function renderComponent() {
    return (
      <div className="addresses">
        <Row className="show-Container">
          <Col sm={12} md={12} lg={12}>
            {isLoading ? (
              <div className="outer-div">
                <div className="sweet-loading inner-div">
                  <PacmanLoader
                    cssOverride={{ display: "block", margin: "0 auto" }}
                    size={20}
                    color="#e03c31"
                    loading={isLoading}
                  />
                </div>
              </div>
            ) : controlType === "table" ? (
              <GenericTable
                columns={columns}
                data={tableData}
                section={`property-integrations-${ctx || "cs"}`}
                pageSize={100}
                meta={{
                  updateData: () => {},
                  handleSave: async () => true,
                  handleDelete: async ({ row }) => {
                    await deleteRecord(dataSet, row.original);
                  },
                }}
              />
            ) : (
              renderList(tableData)
            )}
          </Col>
        </Row>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return <div className="AddressSection">{isAuthenticated ? renderComponent() : null}</div>;
}
